import { useId } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FrameFooter,
  FramePanel,
} from "@volca/ui/components/ui/frame";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { dayjs } from "@/core/utils/date";
import {
  useReportVersions,
  useRestoreVersion,
} from "@/profitability/report-query";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import type { ReportVersion } from "@/profitability/version-types";

const STATUS = {
  published: { label: "Published", color: "success" },
  draft: { label: "Waiting for review", color: "warning" },
  archived: { label: "Earlier", color: "neutral" },
} as const;

const VersionRow = ({
  version,
  busy,
  restoring,
  onView,
  onRestore,
}: {
  version: ReportVersion;
  busy: boolean;
  restoring: boolean;
  onView: (runId: string) => void;
  onRestore: (version: ReportVersion) => void;
}) => {
  const status = STATUS[version.status];
  const when = dayjs(version.published_at ?? version.created_at).format(
    "MMM D, YYYY h:mm a",
  );
  return (
    <FramePanel
      className="flex min-w-0 flex-col gap-3 [overflow-wrap:anywhere]"
      data-testid="template-version"
    >
      <Stack gap={3} className="sm:flex-row sm:items-start sm:justify-between">
        <Stack gap={1} className="min-w-0">
          <Stack direction="row" gap={2} align="center" wrap>
            <Heading level={3} variant="title">
              Version {version.version_number}
            </Heading>
            <Badge variant="secondary" color={status.color}>
              {status.label}
            </Badge>
          </Stack>
          <Text tone="muted" variant="caption">
            {version.published_at ? `Published ${when}` : `Built ${when}`}
          </Text>
        </Stack>
        <Stack direction="row" gap={2} align="center" wrap className="shrink-0">
          {version.reviewed_run_id && (
            <Button
              variant="outline"
              onClick={() => onView(version.reviewed_run_id!)}
            >
              View report
            </Button>
          )}
          {version.status === "archived" && version.reviewed_run_id && (
            <Button
              variant="secondary"
              disabled={busy}
              loading={restoring}
              onClick={() => onRestore(version)}
            >
              Restore as draft
            </Button>
          )}
        </Stack>
      </Stack>
      {version.change_notes.length > 0 && (
        <ul className="list-disc space-y-1 pl-5">
          {version.change_notes.map((note) => (
            <li key={note}>
              <Text>{note}</Text>
            </li>
          ))}
        </ul>
      )}
    </FramePanel>
  );
};

/**
 * Every version of the template, newest first. An earlier one can be opened,
 * or brought back as the draft: it runs again on the files it was reviewed
 * on, and is published like any other draft.
 */
export const VersionsSection = ({
  businessId,
  report,
  onView,
  onRestored,
}: {
  businessId: string;
  report: ProfitabilityReport;
  onView: (runId: string, label: string) => void;
  onRestored: () => void;
}) => {
  const headingId = useId();
  const versions = useReportVersions(businessId, report);
  const restore = useRestoreVersion(businessId);
  const busy = restore.isPending || report.build_status === "building";
  return (
    <Frame
      role="region"
      aria-labelledby={headingId}
      data-testid="versions-section"
    >
      <FrameHeader className="gap-1 py-3">
        <Heading id={headingId} level={2} variant="title">
          Saved versions
        </Heading>
        <FrameDescription className="max-w-prose text-pretty">
          Open a version’s reviewed report, or restore an earlier version as a
          draft.
        </FrameDescription>
      </FrameHeader>
      {versions.isPending ? (
        <FramePanel>
          <Skeleton
            className="h-32 w-full"
            role="status"
            aria-label="Loading versions"
          />
        </FramePanel>
      ) : versions.isError ? (
        <FramePanel>
          <Stack gap={3} align="start">
            <Text tone="destructive" role="alert">
              Versions could not load. {reportError(versions.error)}
            </Text>
            <Button
              variant="outline"
              loading={versions.isFetching}
              onClick={() => void versions.refetch()}
            >
              Try again
            </Button>
          </Stack>
        </FramePanel>
      ) : !versions.data.length ? (
        <FramePanel>
          <Text tone="muted">No saved versions yet.</Text>
        </FramePanel>
      ) : (
        versions.data.map((version) => (
          <VersionRow
            key={version.id}
            version={version}
            busy={busy}
            restoring={
              restore.isPending && restore.variables?.versionId === version.id
            }
            onView={(runId) =>
              onView(runId, `Version ${version.version_number}`)
            }
            onRestore={(chosen) =>
              restore.mutate(
                { reportId: report.id, versionId: chosen.id },
                {
                  onSuccess: () => {
                    toast.success(
                      `Version ${chosen.version_number} is back as the draft. Review it, then publish it.`,
                    );
                    onRestored();
                  },
                  onError: (error) =>
                    toast.error("That version could not be brought back", {
                      description: reportError(error),
                    }),
                },
              )
            }
          />
        ))
      )}
      {report.build_status === "building" && (
        <FrameFooter className="py-2">
          <Text variant="caption" tone="muted">
            A draft is building. You can restore an earlier version after it
            finishes.
          </Text>
        </FrameFooter>
      )}
    </Frame>
  );
};
